'use client'

import { Modal } from "@mantine/core"
import { LoadingSpinner } from "../misc/loadingSpinner"
import ModalTemplate from "./templates/modalTemplate"
import { useModalStore } from "@/context/modalStore";
import { useEffect, useState } from "react";
import { Session } from "next-auth";
import { toast } from "sonner";
import { ICycle } from "@/models/types/cycle";
import CycleFields from "../forms/fields/cycleFields";
import { useStateStore } from "@/context/stateStore";
import { useRouter } from "next/navigation";
import { CreateGrowingPlant } from "@/utils/cycleHelpers/create";
import { useForm } from "@mantine/form";
import { IPlant } from "@/models/types/plant";

export default function NewPlantToGrowModel({ session, handleUpdate, currPlant }: { session: Session | null, handleUpdate: () => Promise<void>, currPlant: IPlant }) {

    const [loading, setLoading] = useState<boolean>(false);
    const router = useRouter();
    const openNewCycleModal = useModalStore(state => state.openNewGrowingPlantModal);
    const setOverlay = useStateStore(state => state.setOverlay);
    const setOpenNewCycleModal = useModalStore(state => state.setOpenNewGrowingPlantModal);

    const cycleForm = useForm({
        mode: 'uncontrolled',
        initialValues: {
            cycle: {
                _id: '',
                name: '',
                userId: '',
                plantId: currPlant !== {} as IPlant && currPlant !== undefined && currPlant !== null && currPlant._id ? currPlant._id : '',
                beginCycleDate: new Date(),
                finishedDate: null,
                imageUrl: '',
                status: 'not started',
                stage: 'seed',
                createdAt: new Date(),
                updatedAt: new Date(),
                type: 'cycle',
            } as ICycle
        },
        validate: {
            cycle: {
                plantId: (value) => !value ? 'Plant ID is required' : null,
            }
        }
    });

    const resetZoom = useStateStore(state => state.handleZoomReset);
    const width = useStateStore(state => state.widthQuery);

    const handleCancel = () => {
        cycleForm.setValues({
            cycle: {
                _id: '',
                name: '',
                userId: '',
                plantId: '',
                beginCycleDate: new Date(),
                finishedDate: null,
                imageUrl: '',
                status: 'not started',
                stage: 'seed',
                createdAt: new Date(),
                updatedAt: new Date(),
                type: 'cycle',
            } as ICycle
        })
        cycleForm.clearErrors();
        cycleForm.resetDirty();
        setOpenNewCycleModal(false);
        setOverlay(false);
        resetZoom(width, false);
        setLoading(false);
    }

    const createCycle = async ({ cycle }: { cycle: ICycle }) => {
        setLoading(true);

        try {
            cycleForm.clearErrors();

            if (!session) {
                toast.warning("You must be signed in!");
                setLoading(false);
                return;
            }

            const sessionUser = session.user;

            if (!sessionUser) {
                toast.error('User session is invalid or missing email');
                setLoading(false);
                return;
            }

            const email = sessionUser.email;

            if (!email || email.trim() === '') {
                toast.error('User email is invalid or missing');
                setLoading(false);
                return;
            }

            const validation = cycleForm.validate();

            if (!validation) {
                setLoading(false);
                return;
            }

            const vals = cycleForm.getValues();
            cycle = vals.cycle;
            const plantId = cycle.plantId ? cycle.plantId : '';
            if (!plantId || plantId.trim() === '') {
                toast.error('Please select a plant');
                setLoading(false);
                return;
            }
            const plantToUse = useStateStore.getState().plants.find(plant => plant._id === plantId);
            const name = plantToUse && plantToUse.name ? plantToUse.name : 'No plant tied to Growing';

            const cycleToPass = {
                name: name,
                userId: email,
                plantId: cycle.plantId ? cycle.plantId : '',
                imageUrl: cycle.imageUrl ? cycle.imageUrl : null,
                beginCycleDate: cycle.beginCycleDate ? new Date(cycle.beginCycleDate) : new Date(),
                finishedDate: cycle.finishedDate ? new Date(cycle.finishedDate) : null,
                status: cycle.status ? cycle.status : 'not started',
                stage: cycle.stage ? cycle.stage : 'seed',
                createdAt: new Date(),
                updatedAt: new Date(),
                type: 'cycle',
            } as ICycle;

            const urlToUse = process.env.NEXT_PUBLIC_BASE_URL ? process.env.NEXT_PUBLIC_BASE_URL as string : '';

            let createCycle = await CreateGrowingPlant({ cycle: cycleToPass, urlToUse: urlToUse, user: sessionUser.email }) as { status: boolean, message: string };

            let attemptStatus = createCycle ? createCycle.status : false;

            if (attemptStatus !== true) {
                toast.error(createCycle.message || 'Error creating growing session');
                setLoading(false);
                return;
            }

            toast.success('Growing session created successfully!');
            await handleUpdate();
            router.refresh();
            handleCancel();
        } catch (error) {
            console.error('Error creating growing session:', error);
            setLoading(false);
            return;
        }
    }

    useEffect(() => {
        if (currPlant !== {} as IPlant && currPlant !== undefined && currPlant !== null && currPlant._id) {
            cycleForm.setValues({
                cycle: {
                    ...cycleForm.values.cycle,
                    plantId: currPlant._id
                }
            });
        } else {
            cycleForm.setValues({
                cycle: {
                    ...cycleForm.values.cycle,
                    plantId: ''
                }
            });
        }
    }, [currPlant]);

    return (
        <Modal
            opened={openNewCycleModal}
            onClose={handleCancel}
            title="New Growing"
            size={'xl'}
            centered
            overlayProps={{
                backgroundOpacity: 0.55, blur: 3, className: 'drop-shadow-xl'
            }}
            lockScroll={true}
            styles={{
                body: { height: '70vh', display: 'flex', flexDirection: 'column' },
                content: { height: 'auto' }
            }}
        >
            <ModalTemplate subtitle={null}>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <form id="modalCycleForm" onSubmit={cycleForm.onSubmit((values) => createCycle({ cycle: values.cycle }))} onAbort={handleCancel} className="w-full">
                        <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-webkit py-1 px-1 sm:py-2 sm:px-2 bg-slate-100/70 rounded-md shadow-[inset_0_2px_8px_rgba(0,0,0,0.10),inset_0_-2px_8px_rgba(0,0,0,0.10)] border border-gray-300">
                            <CycleFields
                                cycleForm={cycleForm}
                            />
                        </div>
                        <section className="flex-shrink-0 flex flex-row w-full justify-evenly items-center pt-3 bg-white border-t border-gray-200 mt-2">
                            <button type="button" onClick={handleCancel} className="cursor-pointer border border-neutral-200 rounded-md hover:bg-neutral-200 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                Cancel
                            </button>
                            <button type='submit' className="cursor-pointer border border-neutral-200 rounded-md hover:bg-blue-200 bg-blue-400 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                Create
                            </button>
                        </section>
                    </form>
                )}
            </ModalTemplate>
        </Modal>
    )
}
